import { useState } from 'react';
import { useSettings, useUpdateSetting } from '../hooks/useSettings.js';
import PageHeader from '../components/PageHeader.jsx';
import Spinner from '../components/Spinner.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';

// Desglosa segundos en días/horas/minutos/segundos y arma un texto natural
// en español — ej. 300 -> "5 minutos", 3600 -> "1 hora".
function formatDuration(totalSeconds) {
  const n = Number(totalSeconds);
  if (!Number.isFinite(n) || n < 0) return null;
  const s = Math.floor(n);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const parts = [];
  if (days) parts.push(`${days} ${days === 1 ? 'día' : 'días'}`);
  if (hours) parts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`);
  if (minutes) parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`);
  if (seconds || parts.length === 0) parts.push(`${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}`);
  return parts.join(' ');
}

function SettingField({ setting }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(setting.value);
  const update = useUpdateSetting();
  const isSecondsField = setting.key.endsWith('_seconds');
  const duration = isSecondsField ? formatDuration(value) : null;

  const handleSave = async () => {
    await update.mutateAsync({ key: setting.key, value });
    setEditing(false);
  };

  return (
    <div className="p-4 bg-bg-elevated rounded-lg border border-border space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-text-primary font-mono">{setting.key}</p>
          <p className="text-xs text-text-muted mt-0.5">{setting.description}</p>
        </div>
        {!editing && (
          <Button variant="ghost" className="text-xs shrink-0" onClick={() => { setValue(setting.value); setEditing(true); }}>
            Editar
          </Button>
        )}
      </div>

      {!editing ? (
        <code className="block text-xs text-text-secondary bg-bg rounded px-2 py-1 break-all">{setting.value}</code>
      ) : (
        <div className="space-y-2">
          <Input value={value} onChange={(e) => setValue(e.target.value)} className="font-mono text-sm" />
          {isSecondsField && (
            <p className="text-xs text-text-muted">{duration ? `≈ ${duration}` : 'Ingresá un número de segundos válido'}</p>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" className="text-xs" onClick={() => setEditing(false)}>Cancelar</Button>
            <Button variant="primary" className="text-xs" loading={update.isPending} onClick={handleSave}>Guardar</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { data, isLoading } = useSettings();

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader title="Configuración operativa" subtitle="Valores que arca-api lee en runtime, editables sin redeploy" />
      <div className="card space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-4"><Spinner /></div>
        ) : (
          <div className="space-y-3">
            {data?.map((s) => <SettingField key={s.key} setting={s} />)}
          </div>
        )}
      </div>
    </div>
  );
}
